import { PieceType, Piece } from './state';

// Tetromino shapes in 4 rotations (0, 90, 180, 270 degrees)
// Each shape is a 4x4 grid stored as [y][x] coordinates relative to piece position

type Shape = number[][];

const SHAPES: Record<PieceType, Shape[]> = {
  // I piece
  1: [
    [[0,0,0,0], [1,1,1,1], [0,0,0,0], [0,0,0,0]],
    [[0,0,1,0], [0,0,1,0], [0,0,1,0], [0,0,1,0]],
    [[0,0,0,0], [0,0,0,0], [1,1,1,1], [0,0,0,0]],
    [[0,1,0,0], [0,1,0,0], [0,1,0,0], [0,1,0,0]],
  ],
  // O piece
  2: [
    [[0,1,1,0], [0,1,1,0], [0,0,0,0], [0,0,0,0]],
    [[0,1,1,0], [0,1,1,0], [0,0,0,0], [0,0,0,0]],
    [[0,1,1,0], [0,1,1,0], [0,0,0,0], [0,0,0,0]],
    [[0,1,1,0], [0,1,1,0], [0,0,0,0], [0,0,0,0]],
  ],
  // T piece
  3: [
    [[0,1,0], [1,1,1], [0,0,0]],
    [[0,1,0], [0,1,1], [0,1,0]],
    [[0,0,0], [1,1,1], [0,1,0]],
    [[0,1,0], [1,1,0], [0,1,0]],
  ],
  // S piece
  4: [
    [[0,1,1], [1,1,0], [0,0,0]],
    [[0,1,0], [0,1,1], [0,0,1]],
    [[0,0,0], [0,1,1], [1,1,0]],
    [[1,0,0], [1,1,0], [0,1,0]],
  ],
  // Z piece
  5: [
    [[1,1,0], [0,1,1], [0,0,0]],
    [[0,0,1], [0,1,1], [0,1,0]],
    [[0,0,0], [1,1,0], [0,1,1]],
    [[0,1,0], [1,1,0], [1,0,0]],
  ],
  // J piece
  6: [
    [[1,0,0], [1,1,1], [0,0,0]],
    [[0,1,1], [0,1,0], [0,1,0]],
    [[0,0,0], [1,1,1], [0,0,1]],
    [[0,1,0], [0,1,0], [1,1,0]],
  ],
  // L piece
  7: [
    [[0,0,1], [1,1,1], [0,0,0]],
    [[0,1,0], [0,1,0], [0,1,1]],
    [[0,0,0], [1,1,1], [1,0,0]],
    [[1,1,0], [0,1,0], [0,1,0]],
  ],
};

// SRS wall kick data
// [from_rotation][to_rotation] = [[dx, dy], ...]
type WallKicks = [number, number][][];

const WALL_KICKS_JLSTZ: WallKicks = [
  // 0 -> 1
  [[ 0, 0], [-1, 0], [-1, 1], [ 0,-2], [-1,-2]],
  // 1 -> 2
  [[ 0, 0], [ 1, 0], [ 1,-1], [ 0, 2], [ 1, 2]],
  // 2 -> 3
  [[ 0, 0], [ 1, 0], [ 1, 1], [ 0,-2], [ 1,-2]],
  // 3 -> 0
  [[ 0, 0], [-1, 0], [-1,-1], [ 0, 2], [-1, 2]],
];

const WALL_KICKS_JLSTZ_CCW: WallKicks = [
  // 0 -> 3
  [[ 0, 0], [ 1, 0], [ 1, 1], [ 0,-2], [ 1,-2]],
  // 1 -> 0
  [[ 0, 0], [ 1, 0], [ 1,-1], [ 0, 2], [ 1, 2]],
  // 2 -> 1
  [[ 0, 0], [-1, 0], [-1, 1], [ 0,-2], [-1,-2]],
  // 3 -> 2
  [[ 0, 0], [-1, 0], [-1,-1], [ 0, 2], [-1, 2]],
];

const WALL_KICKS_I: WallKicks = [
  // 0 -> 1
  [[ 0, 0], [-2, 0], [ 1, 0], [-2,-1], [ 1, 2]],
  // 1 -> 2
  [[ 0, 0], [-1, 0], [ 2, 0], [-1, 2], [ 2,-1]],
  // 2 -> 3
  [[ 0, 0], [ 2, 0], [-1, 0], [ 2, 1], [-1,-2]],
  // 3 -> 0
  [[ 0, 0], [ 1, 0], [-2, 0], [ 1,-2], [-2, 1]],
];

const WALL_KICKS_I_CCW: WallKicks = [
  // 0 -> 3
  [[ 0, 0], [-1, 0], [ 2, 0], [-1, 2], [ 2,-1]],
  // 1 -> 0
  [[ 0, 0], [ 2, 0], [-1, 0], [ 2, 1], [-1,-2]],
  // 2 -> 1
  [[ 0, 0], [ 1, 0], [-2, 0], [ 1,-2], [-2, 1]],
  // 3 -> 2
  [[ 0, 0], [-2, 0], [ 1, 0], [-2,-1], [ 1, 2]],
];

export function getShape(type: PieceType, rotation: number): Shape {
  return SHAPES[type][rotation % 4];
}

export function getShapeSize(type: PieceType): number {
  return type === 1 ? 4 : (type === 2 ? 4 : 3);
}

export function getCells(piece: Piece): [number, number][] {
  const shape = getShape(piece.type, piece.rotation);
  const cells: [number, number][] = [];
  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        cells.push([piece.x + x, piece.y + y]);
      }
    }
  }
  return cells;
}

export function getWallKicks(type: PieceType, fromRotation: number, clockwise: boolean): [number, number][] {
  if (type === 2) return [[0, 0]]; // O piece has no kicks

  if (type === 1) {
    return clockwise ? WALL_KICKS_I[fromRotation] : WALL_KICKS_I_CCW[fromRotation];
  }

  return clockwise ? WALL_KICKS_JLSTZ[fromRotation] : WALL_KICKS_JLSTZ_CCW[fromRotation];
}

export function spawnPiece(type: PieceType): Piece {
  return {
    type,
    x: type === 1 ? 3 : 3, // center on board
    y: type === 1 ? -1 : 0, // spawn above visible area
    rotation: 0,
  };
}
